import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../contexts/TranslationContext";
import { IOfferedSubject } from "../types";
import { extraerNombreMateria } from "../utils";

const collectUniqueSubjectNames = (subjects: IOfferedSubject[] | null | undefined): string[] => {
  const names = new Set<string>();
  if (!Array.isArray(subjects)) return [];
  for (const subject of subjects) {
    for (const section of subject.sections ?? []) {
      const name = extraerNombreMateria(section.section);
      if (name) names.add(name);
    }
  }
  return Array.from(names);
};

const NAMES_SEPARATOR = "\u0000";

export const useSubjectSearchTranslations = (
  subjects: IOfferedSubject[] | null | undefined,
): Map<string, string> => {
  const { lang, enabled, translate } = useTranslation();
  const [titleTranslations, setTitleTranslations] = useState<Map<string, string>>(() => new Map());

  // La clave mantiene estable la identidad de la lista: una oferta nueva con los mismos
  // nombres no debe vaciar el índice ya traducido.
  const uniqueNamesKey = useMemo(
    () => collectUniqueSubjectNames(subjects).join(NAMES_SEPARATOR),
    [subjects],
  );
  const uniqueNames = useMemo(
    () => (uniqueNamesKey ? uniqueNamesKey.split(NAMES_SEPARATOR) : []),
    [uniqueNamesKey],
  );
  const shouldTranslate = enabled && lang === "en" && uniqueNames.length > 0;

  useEffect(() => {
    if (!shouldTranslate) {
      setTitleTranslations(new Map());
      return;
    }

    let cancelled = false;
    setTitleTranslations(new Map());

    uniqueNames.forEach((name) => {
      translate(name)
        .then((translated) => {
          if (cancelled || !translated) return;
          setTitleTranslations((prev) => {
            if (prev.get(name) === translated) return prev;
            const next = new Map(prev);
            next.set(name, translated);
            return next;
          });
        })
        .catch(() => {
          // Sin traducción: el filtro sigue matcheando el título en español.
        });
    });

    return () => {
      cancelled = true;
    };
  }, [shouldTranslate, uniqueNames, translate]);

  return titleTranslations;
};

export default useSubjectSearchTranslations;
